import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { useSections } from "./hooks";
import { SectionContext } from "./Contexts";

export function Section({
    children,
    sectionId,
    onClose,
}: {
    children: ReactNode;
    sectionId: unknown;
    onClose?: () => void;
}) {
    const { active, setActive } = useSections();

    const closeSection = useCallback(() => {
        onClose?.();
        if (active === sectionId) {
            setActive(null);
        }
    }, [onClose, setActive, active, sectionId]);

    const mouseDownPos = useRef<{ x: number; y: number } | null>(null);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!active) return;

        function onMouseDown(ev: MouseEvent) {
            mouseDownPos.current = { x: ev.clientX, y: ev.clientY };
        }

        function onMouseUp(ev: MouseEvent) {
            // If ref is set and the mouse is outside
            if (ref.current && !ref.current.contains(ev.target as Node)) {
                const down = mouseDownPos.current;
                // If no down event, or mouse has moved, do nothing
                if (down && ev.clientX === down.x && ev.clientY === down.y) {
                    closeSection();
                }
            }
            // Reset for next time
            mouseDownPos.current = null;
        }

        document.addEventListener("mousedown", onMouseDown);
        document.addEventListener("mouseup", onMouseUp);

        return () => {
            document.removeEventListener("mousedown", onMouseDown);
            document.removeEventListener("mouseup", onMouseUp);
        };
    }, [active, setActive, ref, closeSection]);

    return (
        <SectionContext.Provider value={{ sectionId, closeSection, ref }}>
            {children}
        </SectionContext.Provider>
    );
}
